import { cn } from "@/shared/utils/cn";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import * as React from "react";

type SwitchSize = "default" | "lg" | "xl";

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  size?: SwitchSize;
}

const sizeVariants: Record<
  SwitchSize,
  { root: string; thumb: string; translate: string }
> = {
  default: {
    root: "w-11 before:h-6",
    thumb: "h-4 w-4",
    translate: "data-[state=checked]:translate-x-5",
  },
  lg: {
    root: "w-12 before:h-7",
    thumb: "h-5 w-5",
    translate: "data-[state=checked]:translate-x-5",
  },
  xl: {
    root: "w-14 before:h-8",
    thumb: "h-6 w-6",
    translate: "data-[state=checked]:translate-x-6",
  },
};

const Switch = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = "default", ...props }, ref) => {
  const variant = sizeVariants[size];

  return (
    <SwitchPrimitives.Root
      className={cn(
        "peer relative inline-flex h-11 min-h-11 min-w-11 shrink-0 cursor-pointer items-center rounded-xl px-1 before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:rounded-full before:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:before:bg-primary data-[state=unchecked]:before:bg-input",
        variant.root,
        className
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          "pointer-events-none relative block rounded-full bg-card ring-0 transition-transform data-[state=unchecked]:translate-x-0",
          variant.thumb,
          variant.translate
        )}
      />
    </SwitchPrimitives.Root>
  );
});
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
